import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Checkbox, TableHead } from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { CourseState, CourseSubjectState } from '../../../../features/types'
import ConfirmActionDialog from '../../../../components/dialogs/ConfirmActionDialog'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import EditCourseSubjectDialog from './EditCourseSubjectDialog'
import { DEFAULT_PAGE_SIZE } from '../../../../utils/constants'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    refresh?: () => void
    activeCourseSubjectsData: CourseSubjectState[]
    course: CourseState
    onDelete: (subject: number) => void
}

const ActiveCourseSubjectsTable: React.FC<Props> = ({
    refresh,
    activeCourseSubjectsData = [],
    course = null,
    onDelete,
}) => {
    const { formatMessage } = useIntl()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE)
    const [currentRowData, setCurrentRowData] = useState(null)
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
    const [openEditDialog, setOpenEditDialog] = useState(false)
    const [updateLanguage, { isLoading: isUpdating }] =
        courseSubjectApiSlice.useUpdateCourseSubjectLanguageMutation()
    const [updateRequired, { isLoading: isUpdatingRequired }] = courseSubjectApiSlice.useUpdateCourseSubjectRequiredMutation()

    const emptyRows =
        page > 0
            ? Math.max(
                0,
                (1 + page) * rowsPerPage - activeCourseSubjectsData.length,
            )
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const toggleLanguage = async (
        courseSubjectId: number,
        languageEnum: string,
        isChecked: boolean,
    ) => {
        try {
            await updateLanguage({
                courseSubjectId,
                languageEnum,
                isChecked,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (error) { }
    }

    const showConfirmDialog = (row: CourseSubjectState, key, value) => {
        setCurrentRowData({ ...row, [key]: value })
        setOpenConfirmDialog(true)
    }

    const confirm = async () => {
        try {
            await updateRequired({
                courseSubjectId: currentRowData?.id,
                isRequired: currentRowData?.isRequired,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (error) { }

        setOpenConfirmDialog(false)
    }

    const showEditDialog = (row: CourseSubjectState) => {
        setCurrentRowData(row)
        setOpenEditDialog(true)
    }

    useEffect(() => {
        if (!openConfirmDialog) {
            setCurrentRowData(null)
            refresh()
        }
    }, [openConfirmDialog])

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>#</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleKz' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleRu' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'edit' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'requiredSubject' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'kazakhGroup' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'russianGroup' })}
                        </TableCell>

                        <TableCell>{formatMessage({ id: 'signin' })}</TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'delete' })}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(rowsPerPage > 0
                        ? activeCourseSubjectsData.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                        )
                        : activeCourseSubjectsData
                    ).map((row, index) => (
                        <TableRow key={row.id}>
                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {index + 1}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.subject?.titleKz}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.subject?.titleRu}
                            </TableCell>

                            <TableCell>
                                <IconButton
                                    sx={{
                                        width: '36px',
                                        height: '36px',
                                    }}
                                    onClick={() => showEditDialog(row)}
                                >
                                    <img src={EditIcon} alt="edit" />
                                </IconButton>
                            </TableCell>

                            <TableCell>
                                <IconButton
                                    sx={{
                                        width: '36px',
                                        height: '36px',
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        color="secondary"
                                        checked={row?.isRequired}
                                        onChange={(e) =>
                                            showConfirmDialog(
                                                row,
                                                'isRequired',
                                                e.target.checked,
                                            )
                                        }
                                    />
                                </IconButton>
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                <IconButton
                                    sx={{
                                        width: '36px',
                                        height: '36px',
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        color="secondary"
                                        checked={row?.languages.includes('KZ')}
                                        disabled={isUpdating}
                                        onChange={(e) =>
                                            toggleLanguage(
                                                row?.id,
                                                'KZ',
                                                e.target.checked,
                                            )
                                        }
                                    />
                                </IconButton>
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                <IconButton
                                    sx={{
                                        width: '36px',
                                        height: '36px',
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        color="secondary"
                                        checked={row?.languages.includes('RU')}
                                        disabled={isUpdating}
                                        onChange={(e) =>
                                            toggleLanguage(
                                                row?.id,
                                                'RU',
                                                e.target.checked,
                                            )
                                        }
                                    />
                                </IconButton>
                            </TableCell>

                            <TableCell>
                                <Link
                                    to={`${row.id}`}
                                    state={{
                                        course: course,
                                        subject: row.subject,
                                    }}
                                >
                                    <IconButton sx={{ background: ui.track }}>
                                        <img
                                            src={ArrowRightIcon}
                                            alt="edit"
                                        />
                                    </IconButton>
                                </Link>
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <ConfirmDelete
                                    confirmAction={() => onDelete(row.id)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={9} />
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={9}
                            count={activeCourseSubjectsData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>

            {openEditDialog && (
                <EditCourseSubjectDialog
                    open={openEditDialog}
                    setOpen={setOpenEditDialog}
                    courseSubject={currentRowData}
                />
            )}
            {openConfirmDialog && (
                <ConfirmActionDialog
                    open={openConfirmDialog}
                    setOpen={setOpenConfirmDialog}
                    confirmAction={confirm}
                    loading={isUpdatingRequired}
                    contentText={formatMessage({ id: 'sureToChangeData' })}
                />
            )}
        </TableContainer>
    )
}

export default ActiveCourseSubjectsTable
